/**
 * The statistics a Longitude Study reports, and the fixed-width block it is written as.
 *
 * <h2>Where the numbers come from</h2>
 *
 * The endpoint behind this study returns dates and nothing else — no counts, no price context. The
 * chart already holds every bar, so the statistics are derived here rather than by asking the
 * backend for data the browser is sitting on.
 *
 * <h2>What each figure means</h2>
 *
 * The report template fixes the labels but not their definitions, so these are stated explicitly
 * and are the things to change if the intended reading differs:
 *
 * - **Total Instances** — event dates that land on a bar in the loaded range. A date outside the
 *   chart is counted separately and called out, never silently folded into the total.
 * - **High / Low Pivot** — the event bar's high is the highest, or its low the lowest, of the
 *   window two bars either side of it. An event within two bars of the chart edge has no such
 *   window and is not counted either way.
 * - **Into Aspect** — the close on the event bar against the close {@link MOVE_WINDOW_BARS} bars
 *   earlier. Higher is L2H (the run into the aspect was upward), lower is H2L.
 * - **Out of Aspect** — the close {@link MOVE_WINDOW_BARS} bars after the event against the close
 *   on the event bar.
 *
 * Percentages are of the instances that could actually be judged, so L2H and H2L sum to 100 for
 * each row. Exactly-flat moves belong to neither and are excluded from the base.
 */

/**
 * Bars either side of the event that define a pivot.
 *
 * Two, and separate from the movement window on purpose: a pivot is a local turning point, which
 * is a statement about its immediate neighbours, while the directional move is a swing measured
 * over a longer run. Widening this one would change what "pivot" means.
 */
export const PIVOT_WINDOW_BARS = 2;

/**
 * Bars looked back and ahead when judging the direction into and out of the aspect.
 *
 * Five. A one- or two-bar move is mostly noise at daily resolution — it reads the last tick rather
 * than the swing the aspect is supposed to mark. Change it here, or pass `moveWindow` per call, to
 * try ten.
 */
export const MOVE_WINDOW_BARS = 5;

/** yyyy-MM-dd from whatever shape a date arrives in. */
function toDayKey(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

/**
 * Statistics for one Longitude Study run.
 *
 * @param {Array<string>} eventDates dates the study found
 * @param {{dates: Array<string>, ohlc: Array<Array<number>>}} barData the loaded chart
 * @returns {object} counts, ready to format
 */
export function computeLongitudeStudyStats(eventDates, barData, options = {}) {
  const pivotWindow = options.pivotWindow != null ? options.pivotWindow : PIVOT_WINDOW_BARS;
  const moveWindow = options.moveWindow != null ? options.moveWindow : MOVE_WINDOW_BARS;

  const dates = (barData && Array.isArray(barData.dates)) ? barData.dates : [];
  const ohlc = (barData && Array.isArray(barData.ohlc)) ? barData.ohlc : [];

  // ECharts candlestick order: [open, close, low, high].
  const closeAt = (i) => (ohlc[i] ? ohlc[i][1] : null);
  const lowAt = (i) => (ohlc[i] ? ohlc[i][2] : null);
  const highAt = (i) => (ohlc[i] ? ohlc[i][3] : null);

  const indexByDate = new Map();
  dates.forEach((d, i) => {
    const key = toDayKey(d);
    // First occurrence wins, so a duplicated bar date cannot shift an event onto a later bar.
    if (!indexByDate.has(key)) indexByDate.set(key, i);
  });

  const stats = {
    totalInstances: 0,
    datesOffChart: 0,
    highPivots: 0,
    lowPivots: 0,
    pivotsNotJudged: 0,
    into: { l2h: 0, h2l: 0, flat: 0, notJudged: 0 },
    outOf: { l2h: 0, h2l: 0, flat: 0, notJudged: 0 },
    pivotWindow,
    moveWindow,
  };

  const seen = new Set();
  for (const raw of (Array.isArray(eventDates) ? eventDates : [])) {
    const key = toDayKey(raw);
    if (!key) continue;
    // The same date twice is one instance; the study can emit a repeat when two crossings fall in
    // one day, and counting it twice would inflate every figure below it.
    if (seen.has(key)) continue;
    seen.add(key);

    const i = indexByDate.get(key);
    if (i === undefined) {
      stats.datesOffChart++;
      continue;
    }
    stats.totalInstances++;

    // --- pivots -----------------------------------------------------------------------------
    if (i - pivotWindow >= 0 && i + pivotWindow < dates.length) {
      let isHigh = true;
      let isLow = true;
      for (let j = i - pivotWindow; j <= i + pivotWindow; j++) {
        if (j === i) continue;
        if (highAt(j) > highAt(i)) isHigh = false;
        if (lowAt(j) < lowAt(i)) isLow = false;
      }
      if (isHigh) stats.highPivots++;
      if (isLow) stats.lowPivots++;
    } else {
      stats.pivotsNotJudged++;
    }

    // --- direction --------------------------------------------------------------------------
    tally(stats.into, closeAt(i - moveWindow), closeAt(i));
    tally(stats.outOf, closeAt(i), closeAt(i + moveWindow));
  }

  stats.into.evaluated = stats.into.l2h + stats.into.h2l;
  stats.outOf.evaluated = stats.outOf.l2h + stats.outOf.h2l;
  return stats;
}

/** Classifies one move as upward (L2H), downward (H2L), flat, or unjudgeable. */
function tally(bucket, from, to) {
  if (from == null || to == null || Number.isNaN(from) || Number.isNaN(to)) {
    bucket.notJudged++;
    return;
  }
  if (to > from) bucket.l2h++;
  else if (to < from) bucket.h2l++;
  else bucket.flat++;
}

function pct(count, base) {
  if (!base) return '  0.0';
  return (count * 100 / base).toFixed(1).padStart(5);
}

/**
 * The report, as a fixed-width block.
 *
 * Column widths are computed from the values rather than hardcoded, so a run with three-digit
 * counts stays aligned instead of pushing one row out of step with the other. Every line is kept
 * under about sixty characters: the journal panel wraps on width, and a wrapped line destroys the
 * alignment this whole format exists for.
 */
export function formatLongitudeStudyReport({ planet1, planet2, distance, frame, stats }) {
  const RULE = '='.repeat(50);
  const SUB = ' ' + '-'.repeat(49);

  const counts = [stats.into.l2h, stats.into.h2l, stats.outOf.l2h, stats.outOf.h2l];
  const w = Math.max(2, ...counts.map(c => String(c).length));

  const cell = (count, base) => `${String(count).padStart(w)} (${pct(count, base)}%)`;

  const lines = [];
  lines.push(RULE);
  lines.push(` ${planet1} - ${distance}° - ${planet2} (${frame})`);
  lines.push(RULE);
  lines.push('');
  lines.push(` Total Instances : ${stats.totalInstances}`);
  lines.push('');
  lines.push(` Pivot Count (+/- ${stats.pivotWindow} bars)`);
  lines.push(SUB);
  lines.push(`   High Pivots   : ${stats.highPivots}`);
  lines.push(`   Low Pivots    : ${stats.lowPivots}`);
  lines.push('');
  // The window is named, as the pivot heading above names its own. It is tunable, so two reports
  // of the same study can differ for no visible reason unless each says what it measured.
  lines.push(` Directional Price Movement (+/- ${stats.moveWindow} bars)`);
  lines.push(SUB);
  lines.push(`   Into Aspect   | L2H: ${cell(stats.into.l2h, stats.into.evaluated)}`
    + `  | H2L: ${cell(stats.into.h2l, stats.into.evaluated)}`);
  lines.push(`   Out of Aspect | L2H: ${cell(stats.outOf.l2h, stats.outOf.evaluated)}`
    + `  | H2L: ${cell(stats.outOf.h2l, stats.outOf.evaluated)}`);

  // Only when there is something to say. A count the report quietly excluded is the kind of thing
  // that turns into "the numbers do not add up" weeks later, so it is stated rather than dropped —
  // but an ordinary run prints the template exactly as specified, with no extra lines.
  const notes = [];
  if (stats.datesOffChart > 0) {
    notes.push(`${stats.datesOffChart} event date(s) fell outside the loaded chart range`);
  }
  if (stats.pivotsNotJudged > 0) {
    notes.push(`${stats.pivotsNotJudged} instance(s) too near the chart edge to judge a pivot`);
  }
  // Worth stating plainly: the directional rows are counted out of the instances that HAVE a bar
  // the window's distance either side, which is fewer than Total Instances near the chart edges.
  // The wider the window the more often that bites, so a reader comparing the row totals against
  // Total Instances needs to be told rather than left to wonder.
  const directionNotJudged = Math.max(stats.into.notJudged, stats.outOf.notJudged);
  if (directionNotJudged > 0) {
    notes.push(`${directionNotJudged} instance(s) lack ${stats.moveWindow} bars either side`);
  }
  if (stats.into.flat > 0 || stats.outOf.flat > 0) {
    notes.push(`${stats.into.flat + stats.outOf.flat} unchanged move(s) excluded from percentages`);
  }
  if (notes.length > 0) {
    lines.push('');
    lines.push(' Notes');
    lines.push(SUB);
    notes.forEach(n => lines.push(`   - ${n}`));
  }

  lines.push('');
  lines.push(RULE);
  return lines.join('\n');
}

/**
 * Convenience: compute and format in one call.
 *
 * @param {object} [options] forwarded to {@link computeLongitudeStudyStats} — `moveWindow` to
 *   change the lookback/lookahead, `pivotWindow` to change what counts as a pivot.
 */
export function buildLongitudeStudyReport({ planet1, planet2, distance, frame, eventDates, barData, options }) {
  const stats = computeLongitudeStudyStats(eventDates, barData, options);
  return { stats, report: formatLongitudeStudyReport({ planet1, planet2, distance, frame, stats }) };
}
